import * as d3 from 'd3';
import {geoMercator, geoPath} from 'd3-geo';
import * as topojson from 'topojson';

export default class Map {
  constructor(svg, height = 350, width = 1000) {
    if (!svg) throw new Error('Base SVG reference not provided');
    
    this.svg = svg;
    this.height = height;
    this.width = width;
    this.activeProvs = new Set();
    this.color = d3.scaleOrdinal().range(d3.schemePaired);
    
    this._initializeMap();
  }

  _initializeMap() {
    this.svg
      .attr('viewBox', `${0} ${0} ${this.width} ${this.height}`)
      .attr('preserveAspectRatio', 'xMinYMin meet');
    
    const projection = geoMercator()
      .scale(700)
      .translate([(this.width)*1.65, (this.height)*2.7]);
    
    this.path = geoPath()
      .projection(projection);

    this.onProvClickCallback = () => {return this.activeProvs};
    this.setOnProvClick();
  }

  drawMap() {
    // Display only Provinces
    const provincesOnly = this.data.objects.canadaprov.geometries.filter(d => {
        if (d.id !== "CA-YT" && d.id !== "CA-NU" && d.id !== "CA-NT") {
          return d.id
        } else {
          return false;
        }
      });
      
    this.data.objects.canadaprov.geometries = provincesOnly;
    this.provGroup = this.svg.selectAll("g")
      .data(topojson.feature(this.data, this.data.objects.canadaprov).features)
      .enter()
      .append("g")
        .attr("class", "provGroup")
        .on('click', this.handleOnProvClick);

    // Draw Provinces
    this.provGroup.append("path")
      .attr("d", this.path)
      .attr("class", "provBorders");

    // Add Province Names, adjust PEI and NB due to collision
    this.provGroup.append("text")
      .attr("class", "mapText")
      .text(d => d.properties.name)
      .attr("x", (d) => {
        if (d.properties.name === "Prince Edward Island") {
          return (this.path.centroid(d)[0] + 55);
        }
        return this.path.centroid(d)[0];
      })
      .attr("y", (d) => {
        if (d.properties.name === "New Brunswick") {
          return (this.path.centroid(d)[1] -10);
        }
        return this.path.centroid(d)[1];
    });
  }

  setOnProvClick(onProvClick) {
    if (onProvClick) {
      this.handleOnProvClick = onProvClick;
      this.onProvClickCallback();
    } else {
      this.handleOnProvClick = (d, i, node) => {
        const selectedProv = d.properties.name,
              provNode = d3.select(node[i]);

        if (this.activeProvs.has(selectedProv) && this.activeProvs.size !== 1) {
          this.activeProvs.delete(selectedProv);
          provNode.classed('active', false);
        } else {
          this.activeProvs.add(selectedProv);
          provNode.classed('active', true);
        }

        this.onProvClickCallback();
      }
    }
  }

  selectUniqueProvince(provName) {
    this.provGroup
      .each((d, i, nodes) => {
        const prov = d.properties.name,
              node = d3.select(nodes[i]);
        
        if (prov !== provName) return;

        if (this.activeProvs.has(prov)) {
          this.activeProvs.delete(prov);
          node.classed('active', false);
        } else {
          this.activeProvs.add(prov);
          node.classed('active', true);
        }

        this.onProvClickCallback();
      });
  }

  selectAllProvinces() {
    this.provGroup
      .each((d, i, nodes) => {
        const prov = d.properties.name,
              node = d3.select(nodes[i]);
        
        if (!node.classed('active')) {
          this.activeProvs.add(prov);
          node.classed('active', true);
        }
      });
  }

  deselectAllProvinces() {
    this.provGroup
      .each((d, i, nodes) => {
        const prov = d.properties.name,
              node = d3.select(nodes[i]);
        
        if (node.classed('active')) {
          this.activeProvs.delete(prov);
          node.classed('active', false);
        }
      });
  }

  setColors(colorArray) {
    this.color = d3.scaleOrdinal()
      .range(colorArray);
  }

  setData(mapData) {
    this.data = mapData;
  }

  clearSVG() {
    this.svg.selectAll('*').remove();
  }
}